import { useEffect, useMemo, useState } from 'react'
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Eyebrow,
  Input,
  MediumText,
  NeutralPanel,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatusText,
  StrongText,
  Subheading,
  SurfaceCard,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui'
import {
  ArrowDownTrayIcon,
  CheckBadgeIcon,
  ChevronDownIcon,
  EyeIcon,
  PencilSquareIcon,
  SparklesIcon,
} from '@heroicons/react/20/solid'
import { DEFAULT_REPORT_FOCUS, REPORT_TEMPLATES } from '../constants'
import { uiClasses } from '../../shared/uiClasses'
import { buildReportPdfFileName, createReportPdfBlob } from '../reportPdf'
import { CustomPdfPreview } from './RecordsVaultTab'
import type {
  ActionResult,
  HealthRecord,
  Medication,
  PersonalProfile,
  Provider,
  Report,
  ReportGenerationRequest,
  ReportRecordScope,
} from '../types'

const TEXT_SUBTLE = uiClasses.textSubtle
const DEFAULT_RECORD_SCOPE: ReportRecordScope = 'all-records'
const DEFAULT_MAX_PRIOR_REPORTS = '5'

const REPORT_RECORD_SCOPE_LABELS: Record<ReportRecordScope, string> = {
  'all-records': 'All records',
  'all-pdfs': 'All uploaded PDFs',
  'parsed-records': 'Parsed records only',
  'parsed-pdfs': 'Parsed uploaded PDFs only',
}

const REPORT_RECORD_SCOPE_OPTIONS: Array<{
  description: string
  label: string
  value: ReportRecordScope
}> = [
  {
    value: 'all-records',
    label: REPORT_RECORD_SCOPE_LABELS['all-records'],
    description: 'Use every local record, regardless of parse status.',
  },
  {
    value: 'all-pdfs',
    label: REPORT_RECORD_SCOPE_LABELS['all-pdfs'],
    description: 'Constrain context to records sourced from PDF uploads.',
  },
  {
    value: 'parsed-records',
    label: REPORT_RECORD_SCOPE_LABELS['parsed-records'],
    description: 'Only use records that have parsed successfully.',
  },
  {
    value: 'parsed-pdfs',
    label: REPORT_RECORD_SCOPE_LABELS['parsed-pdfs'],
    description: 'Use only parsed records from PDF uploads.',
  },
]

function parsePositiveInteger(rawValue: string, fallbackValue: number): number {
  const parsed = Number.parseInt(rawValue, 10)
  if (!Number.isFinite(parsed) || parsed < 1) {
    return fallbackValue
  }

  return Math.min(20, parsed)
}

function isPdfSourcedRecord(record: HealthRecord): boolean {
  return record.source.trim().toLowerCase().includes('pdf')
}

function matchesRecordScope(record: HealthRecord, scope: ReportRecordScope): boolean {
  const isParsed = record.status === 'Parsed'
  const isPdf = isPdfSourcedRecord(record)

  switch (scope) {
    case 'all-records':
      return true
    case 'all-pdfs':
      return isPdf
    case 'parsed-records':
      return isParsed
    case 'parsed-pdfs':
      return isParsed && isPdf
  }
}

function parseReportDateKey(report: Report): string | null {
  if (typeof report.createdAtIso !== 'string') {
    return null
  }

  const normalized = report.createdAtIso.trim()
  if (!normalized) {
    return null
  }

  const parsed = new Date(normalized)
  if (Number.isNaN(parsed.getTime())) {
    return null
  }

  return parsed.toISOString().slice(0, 10)
}

// ─── Main component ──────────────────────────────────────────
interface ReportsTabProps {
  accountEmail: string
  memberName: string
  medications: Medication[]
  onGenerateReport: (request: ReportGenerationRequest) => Promise<void>
  onUpdateReportMetadata: (
    reportId: string,
    metadata: { createdAt: string; focus: string; title: string }
  ) => ActionResult
  personalProfile: PersonalProfile
  providers: Provider[]
  records: HealthRecord[]
  reports: Report[]
}

interface ActiveReportPdfPreview {
  reportId: string
  reportTitle: string
  url: string
}

export default function ReportsTab({
  accountEmail,
  memberName,
  medications,
  onGenerateReport,
  onUpdateReportMetadata,
  personalProfile,
  providers,
  records,
  reports,
}: ReportsTabProps) {
  const reportFocusOptions = useMemo(() => Object.keys(REPORT_TEMPLATES), [])
  const [reportFocus, setReportFocus] = useState(DEFAULT_REPORT_FOCUS)
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false)
  const [recordScope, setRecordScope] = useState<ReportRecordScope>(DEFAULT_RECORD_SCOPE)
  const [includeAiAnalysis, setIncludeAiAnalysis] = useState(true)
  const [includePriorReports, setIncludePriorReports] = useState(true)
  const [priorReportsDateFrom, setPriorReportsDateFrom] = useState('')
  const [priorReportsDateTo, setPriorReportsDateTo] = useState('')
  const [maxPriorReports, setMaxPriorReports] = useState(DEFAULT_MAX_PRIOR_REPORTS)
  const [isGenerating, setIsGenerating] = useState(false)
  const [statusMessage, setStatusMessage] = useState<string | null>(null)
  const [statusTone, setStatusTone] = useState<'danger' | 'success'>('success')
  const [editingReportId, setEditingReportId] = useState<string | null>(null)
  const [metadataDraft, setMetadataDraft] = useState({
    createdAt: '',
    focus: DEFAULT_REPORT_FOCUS,
    title: '',
  })
  const [pdfPreview, setPdfPreview] = useState<ActiveReportPdfPreview | null>(null)
  const [pdfBusyReportId, setPdfBusyReportId] = useState<string | null>(null)

  const selectedRecordCount = useMemo(
    () => records.filter((record) => matchesRecordScope(record, recordScope)).length,
    [recordScope, records]
  )

  const selectedPriorReportCount = useMemo(() => {
    if (!includePriorReports) {
      return 0
    }

    const normalizedMaxPriorReports = parsePositiveInteger(maxPriorReports, 5)
    const hasDateRangeFilter = priorReportsDateFrom.length > 0 || priorReportsDateTo.length > 0
    const filteredReports = reports.filter((report) => {
      if (!hasDateRangeFilter) {
        return true
      }

      const reportDateKey = parseReportDateKey(report)
      if (!reportDateKey) {
        return false
      }

      if (priorReportsDateFrom && reportDateKey < priorReportsDateFrom) {
        return false
      }
      if (priorReportsDateTo && reportDateKey > priorReportsDateTo) {
        return false
      }

      return true
    })

    return filteredReports.slice(0, normalizedMaxPriorReports).length
  }, [includePriorReports, maxPriorReports, priorReportsDateFrom, priorReportsDateTo, reports])

  useEffect(() => {
    return () => {
      if (pdfPreview) {
        URL.revokeObjectURL(pdfPreview.url)
      }
    }
  }, [pdfPreview])

  async function handleGenerateReport() {
    if (isGenerating) {
      return
    }

    if (includePriorReports && priorReportsDateFrom && priorReportsDateTo && priorReportsDateFrom > priorReportsDateTo) {
      setStatusTone('danger')
      setStatusMessage('Start date must be before end date.')
      return
    }

    const normalizedMaxPriorReports = parsePositiveInteger(maxPriorReports, 5)
    const request: ReportGenerationRequest = {
      focus: reportFocus,
      includeAiAnalysis,
      includePriorReports,
      maxPriorReports: normalizedMaxPriorReports,
      priorReportsDateFrom: includePriorReports ? priorReportsDateFrom || null : null,
      priorReportsDateTo: includePriorReports ? priorReportsDateTo || null : null,
      recordScope,
    }

    setStatusMessage(null)
    setIsGenerating(true)
    try {
      await onGenerateReport(request)
      setStatusTone('success')
      const priorReportsMessage = includePriorReports
        ? `${selectedPriorReportCount} prior report(s)`
        : 'no prior report context'
      setStatusMessage(
        `Generated ${reportFocus} report ${includeAiAnalysis ? 'with' : 'without'} AI analysis ` +
          `using ${selectedRecordCount} record(s) and ${priorReportsMessage}.`
      )
    } catch (error) {
      setStatusTone('danger')
      setStatusMessage(error instanceof Error ? error.message : 'Failed to generate report.')
    } finally {
      setIsGenerating(false)
    }
  }

  function openMetadataModal(report: Report) {
    setEditingReportId(report.id)
    setMetadataDraft({
      createdAt: report.createdAt,
      focus: report.focus,
      title: report.title,
    })
    setStatusMessage(null)
  }

  function closeMetadataModal() {
    setEditingReportId(null)
  }

  function saveReportMetadata(reportId: string) {
    const result = onUpdateReportMetadata(reportId, metadataDraft)
    if (!result.ok) {
      setStatusTone('danger')
      setStatusMessage('error' in result ? result.error : 'Failed to update report metadata.')
      return
    }

    setStatusTone('success')
    setStatusMessage('Report metadata updated.')
    setEditingReportId(null)
  }

  async function handlePreviewReportPdf(report: Report) {
    if (pdfBusyReportId) {
      return
    }

    setPdfBusyReportId(report.id)
    setStatusMessage(null)

    try {
      const pdfBlob = await createReportPdfBlob(report, {
        accountEmail,
        medications,
        memberName,
        personalProfile,
        providers,
      })
      const previewUrl = URL.createObjectURL(pdfBlob)

      setPdfPreview((currentPreview) => {
        if (currentPreview) {
          URL.revokeObjectURL(currentPreview.url)
        }

        return {
          reportId: report.id,
          reportTitle: report.title,
          url: previewUrl,
        }
      })
    } catch (error) {
      setStatusTone('danger')
      setStatusMessage(error instanceof Error ? error.message : 'Failed to render report PDF preview.')
    } finally {
      setPdfBusyReportId(null)
    }
  }

  async function handleDownloadReportPdf(report: Report) {
    if (pdfBusyReportId) {
      return
    }

    setPdfBusyReportId(report.id)
    setStatusMessage(null)

    try {
      const pdfBlob = await createReportPdfBlob(report, {
        accountEmail,
        medications,
        memberName,
        personalProfile,
        providers,
      })
      const downloadUrl = URL.createObjectURL(pdfBlob)
      const fileName = buildReportPdfFileName(report)
      const linkElement = document.createElement('a')
      linkElement.href = downloadUrl
      linkElement.download = fileName
      document.body.appendChild(linkElement)
      linkElement.click()
      linkElement.remove()
      window.setTimeout(() => URL.revokeObjectURL(downloadUrl), 0)

      setStatusTone('success')
      setStatusMessage(`Downloaded PDF report: ${fileName}`)
    } catch (error) {
      setStatusTone('danger')
      setStatusMessage(error instanceof Error ? error.message : 'Failed to download report PDF.')
    } finally {
      setPdfBusyReportId(null)
    }
  }

  function closePdfPreview() {
    setPdfPreview((currentPreview) => {
      if (currentPreview) {
        URL.revokeObjectURL(currentPreview.url)
      }
      return null
    })
  }

  const isAnyPdfBusy = pdfBusyReportId !== null

  return (
    <div className="space-y-6">
      {/* ── Generate report ───────────────────────────────────── */}
      <SurfaceCard as="article">
        <Subheading variant="dashboard">Generate report</Subheading>

        <div className="mt-4 space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-0 flex-1">
              <Eyebrow as="label" variant="mini" tone="subtle">Report focus</Eyebrow>
              <Select
                value={reportFocus}
                onValueChange={setReportFocus}
                disabled={isGenerating}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {reportFocusOptions.map((focus) => (
                    <SelectItem key={focus} value={focus}>
                      {focus}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button className="shrink-0 justify-center whitespace-nowrap" onClick={() => void handleGenerateReport()} disabled={isGenerating}>
              <SparklesIcon data-slot="icon" />
              {isGenerating ? 'Generating...' : 'Generate report'}
            </Button>
          </div>

          <NeutralPanel tone="muted" className="overflow-hidden" style={{ padding: 0 }}>
            <Button
              type="button"
              variant="unstyled"
              className="reports-advanced-toggle"
              onClick={() => setShowAdvancedSettings((current) => !current)}
              disabled={isGenerating}
            >
              <span>Advanced settings</span>
              <ChevronDownIcon
                className="reports-advanced-chevron"
                style={{ transform: showAdvancedSettings ? 'rotate(180deg)' : undefined }}
              />
            </Button>

            {showAdvancedSettings && (
              <div className="reports-advanced-body">
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="flex items-center justify-between gap-3">
                    <MediumText as="span" tone="secondary">AI analysis</MediumText>
                    <Switch checked={includeAiAnalysis} onCheckedChange={setIncludeAiAnalysis} variant="accent" />
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <MediumText as="span" tone="secondary">Include prior reports</MediumText>
                    <Switch checked={includePriorReports} onCheckedChange={setIncludePriorReports} variant="accent" />
                  </div>
                </div>

                <Select
                  value={recordScope}
                  onValueChange={(nextValue) => setRecordScope(nextValue as ReportRecordScope)}
                  disabled={isGenerating}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {REPORT_RECORD_SCOPE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {includePriorReports && (
                  <div className="grid gap-3 sm:grid-cols-3">
                    <div>
                      <Eyebrow as="label" variant="mini" tone="subtle">From date</Eyebrow>
                      <Input
                        type="date"
                        className="mt-1"
                        value={priorReportsDateFrom}
                        onChange={(event) => setPriorReportsDateFrom(event.target.value)}
                        disabled={isGenerating}
                      />
                    </div>
                    <div>
                      <Eyebrow as="label" variant="mini" tone="subtle">To date</Eyebrow>
                      <Input
                        type="date"
                        className="mt-1"
                        value={priorReportsDateTo}
                        onChange={(event) => setPriorReportsDateTo(event.target.value)}
                        disabled={isGenerating}
                      />
                    </div>
                    <div>
                      <Eyebrow as="label" variant="mini" tone="subtle">Max prior reports</Eyebrow>
                      <Input
                        type="number"
                        min={1}
                        max={20}
                        className="mt-1"
                        value={maxPriorReports}
                        onChange={(event) => setMaxPriorReports(event.target.value)}
                        disabled={isGenerating}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </NeutralPanel>

          {statusMessage && (
            <StatusText tone={statusTone === 'danger' ? 'danger' : 'success'} aria-live="polite">
              {statusMessage}
            </StatusText>
          )}
        </div>
      </SurfaceCard>

      {/* ── Report history ────────────────────────────────────── */}
      <SurfaceCard as="article">
        <Subheading variant="dashboard">Latest reports</Subheading>
        <Table
          className="records-vault-table mt-4"
          surface
          gutter="none"
          dense
        >
          <TableHeader className="records-vault-table-head">
            <TableRow>
              <TableHead className="records-vault-table-col">Report</TableHead>
              <TableHead className="records-vault-table-col">Focus</TableHead>
              <TableHead className="records-vault-table-col">Analysis</TableHead>
              <TableHead className="records-vault-table-col text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {reports.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4}>
                  <div className="p-1">
                    <MediumText tone="secondary">No reports generated yet.</MediumText>
                    <p className={`mt-1 text-xs/5 ${TEXT_SUBTLE}`}>
                      Generate a report above to create a PDF report entry.
                    </p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              reports.map((report) => {
                const hasAiAnalysis = report.includesAiAnalysis !== false

                return (
                  <TableRow key={report.id}>
                    <TableCell>
                      <div className="min-w-0">
                        <StrongText className="truncate" title={report.title}>
                          {report.title}
                        </StrongText>
                        <p className={`text-xs/5 ${TEXT_SUBTLE}`}>{report.createdAt}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="zinc">{report.focus}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Badge variant={hasAiAnalysis ? 'green' : 'zinc'}>
                          {hasAiAnalysis ? 'AI' : 'No AI'}
                        </Badge>
                        {hasAiAnalysis && (
                          <span className={`flex items-center gap-1 text-xs/5 ${TEXT_SUBTLE}`}>
                            <CheckBadgeIcon className="size-4 text-accent" />
                            {report.confidence}%
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell
                      className="text-right"
                      onClick={(event) => event.stopPropagation()}
                    >
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          type="button"
                          variant="unstyled"
                          className="reports-table-action"
                          title="Preview PDF"
                          disabled={isAnyPdfBusy}
                          onClick={() => void handlePreviewReportPdf(report)}
                        >
                          <EyeIcon className="size-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="unstyled"
                          className="reports-table-action"
                          title="Download PDF"
                          disabled={isAnyPdfBusy}
                          onClick={() => void handleDownloadReportPdf(report)}
                        >
                          <ArrowDownTrayIcon className="size-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="unstyled"
                          className="reports-table-action"
                          title="Edit metadata"
                          onClick={() => openMetadataModal(report)}
                        >
                          <PencilSquareIcon className="size-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
      </SurfaceCard>

      {/* ── PDF preview modal ─────────────────────────────────── */}
      {pdfPreview && (
        <Dialog open onOpenChange={(open) => !open && closePdfPreview()}>
            <DialogContent size="xl" padding="compact">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <DialogTitle className="truncate">
                    {pdfPreview.reportTitle}
                  </DialogTitle>
                  <p className={`text-xs/5 ${TEXT_SUBTLE}`}>PDF report preview</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <Button
                      type="button"
                      variant="unstyled"
                      className="hover:text-zinc-900 dark:hover:text-zinc-100"
                      onClick={closePdfPreview}
                    >
                      <MediumText as="span" size="xs" tone="subtle">Close</MediumText>
                    </Button>
                </div>
              </div>
              <div className="mt-3">
                <CustomPdfPreview previewUrl={pdfPreview.url} recordTitle={pdfPreview.reportTitle} />
              </div>
            </DialogContent>
        </Dialog>
      )}

      {/* ── Metadata edit modal ───────────────────────────────── */}
      {editingReportId && (
        <Dialog open onOpenChange={(open) => !open && closeMetadataModal()}>
            <DialogContent>
              <div className="flex items-start justify-between gap-4">
                <DialogTitle>
                  Edit report metadata
                </DialogTitle>
                <Button
                  type="button"
                  variant="unstyled"
                  className="hover:text-zinc-900 dark:hover:text-zinc-100"
                  onClick={closeMetadataModal}
                >
                  <MediumText as="span" size="xs" tone="subtle">Close</MediumText>
                </Button>
              </div>

              <div className="mt-5 space-y-3">
                <div>
                  <Eyebrow as="label" htmlFor="modal-report-title" variant="mini" tone="subtle">Title</Eyebrow>
                  <Input
                    id="modal-report-title"
                    className="mt-1"
                    value={metadataDraft.title}
                    onChange={(event) =>
                      setMetadataDraft((current) => ({ ...current, title: event.target.value }))
                    }
                  />
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <Eyebrow as="label" htmlFor="modal-report-focus" variant="mini" tone="subtle">Focus</Eyebrow>
                    <Select
                      value={metadataDraft.focus}
                      onValueChange={(nextValue) =>
                        setMetadataDraft((current) => ({ ...current, focus: nextValue }))
                      }
                    >
                      <SelectTrigger id="modal-report-focus" className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {reportFocusOptions.map((focus) => (
                          <SelectItem key={focus} value={focus}>
                            {focus}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Eyebrow as="label" htmlFor="modal-report-date" variant="mini" tone="subtle">Date label</Eyebrow>
                    <Input
                      id="modal-report-date"
                      className="mt-1"
                      value={metadataDraft.createdAt}
                      onChange={(event) =>
                        setMetadataDraft((current) => ({ ...current, createdAt: event.target.value }))
                      }
                      placeholder="e.g. 5m ago"
                    />
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2 pt-2">
                  <Button type="button" onClick={() => saveReportMetadata(editingReportId)}>
                    Save metadata
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className=""
                    onClick={closeMetadataModal}
                  >
                    Cancel
                  </Button>
                </div>
              </div>

              {statusMessage && statusTone === 'danger' && (
                <StatusText className="mt-3" tone="danger" aria-live="polite">
                  {statusMessage}
                </StatusText>
              )}
            </DialogContent>
        </Dialog>
      )}
    </div>
  )
}
